import React, {useEffect, useRef, useState} from 'react';
import styled from 'styled-components';
import {BUTTON_COLOR, BUTTON_STYLE} from '../../../constants';
import Util from '../../../forecast-app/shared/util/util';
import TopUpAiCreditsMutation from '../../../mutations/mission_control/top_up_ai_credits_mutation';
import GenericModal from '../../modal/generic_modal';
import {formatPack} from './format_pack';

export const REASON_MAX_LENGTH = 500;
export const GENERIC_FAILURE_MESSAGE = 'Something went wrong while topping up AI credits. Please try again.';

const TopUpModalStyle = styled.div`
	min-width: 480px;

	textarea {
		width: 100%;
		min-height: 90px;
		margin-top: 4px;
		border: 1px solid black;
	}

	.top-up-error {
		margin-top: 12px;
		color: #c0392b;
	}
`;

export const TopUpModal = ({company, closeModal, onSuccess}) => {
	const [reason, setReason] = useState('');
	const [inFlight, setInFlight] = useState(false);
	const [errorMessage, setErrorMessage] = useState(null);
	// Guards against a second commit before the first one's state update has rendered, and against
	// state updates after the modal was dismissed while the request was still running.
	const inFlightRef = useRef(false);
	const mountedRef = useRef(true);

	useEffect(() => {
		mountedRef.current = true;
		return () => {
			mountedRef.current = false;
		};
	}, []);

	const finish = message => {
		inFlightRef.current = false;
		if (mountedRef.current) {
			setInFlight(false);
			setErrorMessage(message);
		}
	};

	const confirm = () => {
		const trimmedReason = reason.trim();
		if (inFlightRef.current || !trimmedReason) {
			return;
		}
		inFlightRef.current = true;
		setInFlight(true);
		setErrorMessage(null);

		Util.CommitMutation(
			TopUpAiCreditsMutation,
			{companyId: company.companyId, reason: trimmedReason},
			response => {
				const payload = response && response.TopUpAiCredits;
				if (!payload) {
					finish(GENERIC_FAILURE_MESSAGE);
				} else if (payload.success === true) {
					inFlightRef.current = false;
					// The toast still reports a grant that lands after this modal was dismissed, but
					// closeModal is the parent's shared close, so calling it then would shut whichever
					// top-up modal is open now.
					onSuccess(payload);
					if (mountedRef.current) {
						closeModal();
					}
				} else {
					finish(payload.errorMessage || GENERIC_FAILURE_MESSAGE);
				}
			},
			false,
			null,
			// Handled here rather than by the shared failure modal so the modal stays open with a
			// message and the operator can retry.
			() => finish(GENERIC_FAILURE_MESSAGE)
		);
	};

	const content = (
		<TopUpModalStyle>
			<div>
				Company: <b>{company.companyName}</b>
			</div>
			<div>
				Pack: <b>{formatPack(company.aiCreditPack, company.aiCreditPackCredits)}</b>
			</div>
			<br />
			<label htmlFor="ai-credits-top-up-reason">Reason (required)</label>
			<textarea
				id="ai-credits-top-up-reason"
				data-cy="ai-credits-top-up-reason"
				maxLength={REASON_MAX_LENGTH}
				value={reason}
				onChange={event => setReason(event.target.value)}
			/>
			<div>
				{reason.length}/{REASON_MAX_LENGTH}
			</div>
			{errorMessage ? (
				<div className="top-up-error" role="alert" data-cy="ai-credits-top-up-error">
					{errorMessage}
				</div>
			) : null}
		</TopUpModalStyle>
	);

	const buttons = [
		{
			text: 'Cancel',
			style: BUTTON_STYLE.FILLED,
			color: BUTTON_COLOR.WHITE,
		},
		{
			text: 'Confirm',
			cy: 'ai-credits-top-up-confirm',
			style: BUTTON_STYLE.FILLED,
			color: BUTTON_COLOR.GREEN,
			disabled: inFlight || !reason.trim(),
			preventDefaultClose: true,
			callback: confirm,
		},
	];

	return (
		<GenericModal
			closeModal={closeModal}
			buttons={buttons}
			content={content}
			headerText={`Top up AI credits for ${company.companyName}`}
		/>
	);
};
